import type { GrowthDeclineDirection, GrowthDeclineIndicatorProps, GrowthDeclineSize } from './types';

// ─── Icons ────────────────────────────────────────────────────────────────────

function ArrowUpIcon({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  );
}

function MinusIcon({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Resolves whether the current state is visually positive (green) or negative (red).
// positive=true (default): up = good, down = bad (sales/revenue)
// positive=false (inverse): up = bad, down = good (expenses/costs)
function isVisuallyPositive(direction: GrowthDeclineDirection, positive: boolean): boolean | null {
  if (direction === 'flat') return null;
  return positive ? direction === 'up' : direction === 'down';
}

const SIZE_ICON: Record<GrowthDeclineSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
};

const SIZE_TEXT: Record<GrowthDeclineSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
};

const SIZE_LABEL: Record<GrowthDeclineSize, string> = {
  sm: 'text-[10px]',
  md: 'text-xs',
};

// ─── GrowthDeclineIndicator ───────────────────────────────────────────────────

export function GrowthDeclineIndicator({
  direction,
  value,
  positive = true,
  label,
  size = 'sm',
  animate = false,
}: GrowthDeclineIndicatorProps) {
  const visuallyPositive = isVisuallyPositive(direction, positive);

  const colorClass =
    visuallyPositive === null
      ? 'text-neutral-500 dark:text-neutral-400'
      : visuallyPositive
        ? 'text-success-500 dark:text-success-400'
        : 'text-danger-500 dark:text-danger-400';

  const iconClass = `${SIZE_ICON[size]} ${animate ? 'transition-transform duration-300' : ''}`;

  const ariaLabel = [
    direction === 'up' ? 'Up' : direction === 'down' ? 'Down' : 'Flat',
    value,
    label ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={`inline-flex items-center gap-0.5 ${colorClass} ${animate ? 'animate-fade-in' : ''}`}
      aria-label={ariaLabel}
    >
      {/* Icon */}
      {direction === 'up' && <ArrowUpIcon className={iconClass} />}
      {direction === 'down' && <ArrowDownIcon className={iconClass} />}
      {direction === 'flat' && <MinusIcon className={iconClass} />}

      {/* Value */}
      <span className={`font-semibold tabular-nums ${SIZE_TEXT[size]}`}>
        {value}
      </span>

      {/* Comparison label */}
      {label && (
        <span className={`font-normal text-neutral-400 dark:text-neutral-500 ${SIZE_LABEL[size]}`}>
          {label}
        </span>
      )}
    </span>
  );
}
